// this component represents body of the segment listing page
// it's rendered when all required data for the listing page are loaded correctly
import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { DynamicContent } from '@inspiration/ufo-blocks';
import { resolveByCountry } from '@codex/helpers';
import { Button } from '@codex/basics';

import styled from 'styled-components';
import LayoutWrapper from '../PageLayoutComponents/LayoutWrapper';
import ConnectedLinksAboveBreadcrumb from '../TopLevelContainers/ConnectedLinksAboveBreadcrumb';
import ConnectedBreadcrumb from '../TopLevelContainers/ConnectedBreadcrumb';
import ConnectedHeaderTitle from '../TopLevelContainers/ConnectedHeaderTitle';
import ConnectedHeaderBanner from '../TopLevelContainers/ConnectedHeaderBanner';
import FiltersContainerDesktop from '../Filtering/FiltersContainerDesktop';
import FiltersContainerTabletMobile from '../Filtering/FiltersContainerTabletMobile';
import ConnectedResultsMessage from '../TopLevelContainers/ConnectedResultsMessage';
import { getActiveListingPage } from '../../reduck/selectors';
import ConnectedSortBy from '../TopLevelContainers/ConnectedSortBy';
import ConnectedItemsPerPage from '../TopLevelContainers/ConnectedItemsPerPage';
import ConnectedViewSwitch from '../TopLevelContainers/ConnectedViewSwitch';
import ConnectedComparePanel from '../TopLevelContainers/ConnectedComparePanel';
import ConnectedProductListing from '../TopLevelContainers/ConnectedProductListing';
import ConnectedPaginator from '../TopLevelContainers/ConnectedPaginator';
import CreditRepresentativeExamplePanelWrapper from '../CreditRepresentativeExamplePanel/CreditRepresentativeExamplePanel';
import BackToTop from '../BackToTop/BackToTop';
import ConnectedRichRelevanceBlock from '../TopLevelContainers/ConnectedRichRelevanceBlock';
import ConnectedComparePopup from '../TopLevelContainers/ConnectedComparePopup';
import LocationSearchBlockWrapper from '../TopLevelContainers/ConnectedLocationSearchBlock';
import UISettings from '../../settings/UISettings';

import {
  ListingPageContent,
  AsideFilters,
  ListingPageOverlay,
  FilterResultsMessageContainer,
  ListPageFilterButton,
  SortByWrapper,
  ItemsPerPage,
  SortForm
} from '../ListingPageStyled';

const LocationSearchBlock = styled.div`
  margin-left: 2%;
  position: relative;
  margin-bottom: 10px;
`;

const WrapperListPageViewSwitch = styled.div`
  height: 1px;
`;

const HiddenButtonToAvoidCLS = styled(Button)`
  width: 0px;
  visibility: hidden;
`;

const SegmentListingPageBody = ({
  serverSideRendering,
  isDesktop,
  isTabletOrMobile,
  leftPanelBelowFiltersContent,
  loadingProducts,
  renderSortingAndViewSwitchingAndItemsPerPage,
  bottomPanelBelowPaginatorContent,
  comparePopupOpened,
  renderCreditRepresentativeExample
}) => {
  const resultsMessage = useRef(null);
  const [previousLoadingProductsPropValue, setPreviousLoadingProductsPropValue] = useState(
    loadingProducts
  );

  useEffect(() => {
    if (!loadingProducts && previousLoadingProductsPropValue && resultsMessage.current) {
      // when new products on page are loaded, the page is scrolled to the top of the products
      resultsMessage.current.scrollIntoView({ behavior: 'smooth' });
    }

    setPreviousLoadingProductsPropValue(loadingProducts);
  }, [loadingProducts]);

  const renderGoogleTagElements = resolveByCountry(true, false);

  return (
    <LayoutWrapper wrapperName={'body'}>
      <ConnectedLinksAboveBreadcrumb />
      <ListingPageContent id={'content'}>
        <ConnectedBreadcrumb />
        <span data-component={'pageTitle'}>
          <ConnectedHeaderTitle />
        </span>
        <span id={'vistaprocessing'}>{/* TODO: check why this span is here */}</span>
        <div data-component={'headerBanner'}>
          <ConnectedHeaderBanner />
        </div>
        <div className={'row'}>
          <AsideFilters id={'filters'} className={'col3 fixed'}>
            <div className={'row nosp'}>
              <section className={'col12'} data-component={'listing-page-filters'}>
                {(serverSideRendering || isDesktop) && <FiltersContainerDesktop />}
              </section>
            </div>
            {leftPanelBelowFiltersContent && (
              <div className={'row'}>
                <section id={'banners'} className={'desktop col12'}>
                  <DynamicContent root={leftPanelBelowFiltersContent} />
                </section>
              </div>
            )}
            {renderGoogleTagElements && (
              <>
                <div
                  className={'desktop ad center mod'}
                  id={'div-gpt-ad-wideskytop-1'}
                  style={{ height: '600px' }}
                />
                <div
                  className={'desktop ad center mod'}
                  id={'div-gpt-ad-wideskybot-2'}
                  style={{ height: '600px' }}
                />
              </>
            )}
          </AsideFilters>
          <section role={'main'} className={'col9'} data-search-engine-used={'new engine'}>
            {loadingProducts ? <ListingPageOverlay className={'listing-page-overlay'} /> : null}
            {renderGoogleTagElements && (
              <div
                className={'desktop ad center mod'}
                id={'div-gpt-ad-leadertop-0'}
                style={{ height: '90px' }}
              />
            )}
            <div className={'row nosp'}>
              <FilterResultsMessageContainer className={'filter-results-message-container'}>
                <ListPageFilterButton className={'list-page-filter-button'}>
                  <HiddenButtonToAvoidCLS small>&nbsp;</HiddenButtonToAvoidCLS>
                  {isTabletOrMobile && <FiltersContainerTabletMobile />}
                </ListPageFilterButton>
                <div data-component={'list-page-results-message'} ref={resultsMessage}>
                  <ConnectedResultsMessage />
                </div>
              </FilterResultsMessageContainer>
              {renderSortingAndViewSwitchingAndItemsPerPage && (
                <SortForm id={'sortForm'}>
                  <SortByWrapper className={'dc-sort-by-wrapper'}>
                    <div data-component={'list-page-sort-by'}>
                      <ConnectedSortBy />
                    </div>
                  </SortByWrapper>
                  <ItemsPerPage className={'itemsPerPage'} data-component={'itemsPerPage'}>
                    <ConnectedItemsPerPage />
                  </ItemsPerPage>
                  <WrapperListPageViewSwitch data-component={'list-page-view-switch'}>
                    <ConnectedViewSwitch />
                  </WrapperListPageViewSwitch>
                </SortForm>
              )}
            </div>
            <form
              method={'POST'}
              data-component={'compareForm'}
              action={`./${resolveByCountry('gbuk', 'ieen')}/s_action/compare/index.html`}
            >
              <div data-component={'compare-panel-wrapper'}>
                <ConnectedComparePanel />
              </div>
              <div className={'row tsp'} data-component={'list-page-product-list'}>
                <LocationSearchBlock data-anonid={'location-search-block'}>
                  {/* Postcode checker is rendered only on client */}
                  {!serverSideRendering && (
                    <LocationSearchBlockWrapper name={'above product listing'} />
                  )}
                </LocationSearchBlock>
                <ConnectedProductListing />
              </div>
            </form>
            <div className={'row tsp center'} data-component={'list-page-paginator'}>
              <ConnectedPaginator />
            </div>
            {renderCreditRepresentativeExample && <CreditRepresentativeExamplePanelWrapper />}
            {bottomPanelBelowPaginatorContent && (
              <DynamicContent root={bottomPanelBelowPaginatorContent} />
            )}
            {(serverSideRendering || isDesktop) && <BackToTop isDesktop />}
          </section>
        </div>
        {/* Rich Relevance Block is rendered only on client */}
        {!serverSideRendering && (
          <section>
            <ConnectedRichRelevanceBlock />
          </section>
        )}
      </ListingPageContent>
      {isTabletOrMobile && !comparePopupOpened && <BackToTop isDesktop={false} />}
      <ConnectedComparePopup />
    </LayoutWrapper>
  );
};

SegmentListingPageBody.displayName = 'SegmentListingPageBody';

SegmentListingPageBody.propTypes = {
  serverSideRendering: PropTypes.bool.isRequired,
  isDesktop: PropTypes.bool.isRequired,
  isTabletOrMobile: PropTypes.bool.isRequired,
  leftPanelBelowFiltersContent: PropTypes.shape({}),
  loadingProducts: PropTypes.bool.isRequired,
  renderSortingAndViewSwitchingAndItemsPerPage: PropTypes.bool.isRequired,
  bottomPanelBelowPaginatorContent: PropTypes.shape({}),
  comparePopupOpened: PropTypes.bool.isRequired,
  renderCreditRepresentativeExample: PropTypes.bool.isRequired
};

SegmentListingPageBody.defaultProps = {
  leftPanelBelowFiltersContent: undefined,
  bottomPanelBelowPaginatorContent: undefined
};

const mapStateToProps = (state) => {
  const deviceType = state.listingPage.browser
    ? state.listingPage.browser.device.deviceType
    : undefined;
  const noProductsOnListingPage = getActiveListingPage(state).productList.length === 0;

  const isOnLineCreditEnabled =
    UISettings.get('component.onlinecredit.online_credit_active') !== false;

  return {
    isDesktop: deviceType === 'desktop' || deviceType === 'wide',
    isTabletOrMobile: deviceType === 'tablet' || deviceType === 'mobile',
    serverSideRendering: typeof deviceType === 'undefined',
    leftPanelBelowFiltersContent: state.listingPage.staticData?.leftPanelBelowFiltersContent,
    loadingProducts: state.listingPage.commonData.xhrStatus.pendingProductsRequest,
    renderSortingAndViewSwitchingAndItemsPerPage: !noProductsOnListingPage,
    bottomPanelBelowPaginatorContent: state.listingPage.staticData.bottomPanelBelowPaginatorContent,
    comparePopupOpened:
      state.listingPage.comparePopup.loading ||
      !!state.listingPage.comparePopup.compareProductsData,
    renderCreditRepresentativeExample: !noProductsOnListingPage && isOnLineCreditEnabled
  };
};

export default connect(mapStateToProps)(SegmentListingPageBody);
